import { FC, useCallback, useMemo } from 'react';
import { Autocomplete, Box, Button, FormHelperText, IconButton, Stack, TextField } from '@mui/material';
import useServiceOptions from '../../../../hooks/options/useServiceOptions';
import { IService, ServiceLocationType, ServiceOption } from '../../../../models/IService';
import NumberFormat, { SourceInfo } from 'react-number-format';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import RemoveCircleOutlineOutlinedIcon from '@mui/icons-material/RemoveCircleOutlineOutlined';
import { FormikErrors, FormikTouched } from 'formik';
import { getDefaultServicePrice, getMaterialsBasedServicePrice } from '../../../../utils/services';
import { IEmployee } from '../../../../models/IEmployee';
import FocusTextField from '../../../FocusTextField';
import { AppointmentServiceRowType } from '../../../../models/IAppointment';
import SectionHeading from '../../../SectionHeading';
import AppointmentServiceOptions from '../appointment-service-options';

interface IAppointmentServicesSubformProps {
    value: Array<AppointmentServiceRowType>;
    setValue: (field: string, value: any) => void;
    setTouched: (field: string, touched?: boolean, shouldValidate?: boolean) => void;
    valuePrefix: string;
    useMultiservices: boolean;
    errors?: FormikErrors<AppointmentServiceRowType>[] | string | string[];
    touched?: FormikTouched<AppointmentServiceRowType[][number]>[] | null | boolean;
    disabled?: boolean;
    employeeServices?: IEmployee['services'];
    serviceSelectCb?: (service: IService) => void;
}

const AppointmentServicesSubform: FC<IAppointmentServicesSubformProps> = ({
    value,
    setValue,
    setTouched,
    valuePrefix,
    useMultiservices,
    errors,
    touched,
    disabled,
    employeeServices,
    serviceSelectCb
}) => {
    const { services, isLoading } = useServiceOptions();

    const handleChangeService = useCallback(
        (v: IService | null, index: number) => {
            if (v) {
                const isMaterialBasedService = !!v?.use_materials;
                const price = getDefaultServicePrice(v, employeeServices || []);
                setValue(`${valuePrefix}.${index}.service`, v);
                setValue(`${valuePrefix}.${index}.options`, []);
                setValue(`${valuePrefix}.${index}.price`, price ? parseFloat(String(price)) : 0);
                setValue(`${valuePrefix}.${index}.prepay`, v.prepay ? parseFloat(String(v.prepay)) : null);
                setValue(`${valuePrefix}.${index}.materials_amount`, isMaterialBasedService ? 0 : undefined);
                setTouched(`${valuePrefix}.${index}.materials_amount`, false);
                serviceSelectCb && serviceSelectCb(v);
            }
        },
        [employeeServices, serviceSelectCb, setTouched, setValue, valuePrefix]
    );

    const handlePriceChange = useCallback(
        (index: number, price: number | undefined | null, isChangedManually: boolean) => {
            setValue(`${valuePrefix}.${index}.price`, price);
            if (isChangedManually) {
                setValue(`${valuePrefix}.${index}.materials_amount`, 0);
            }
        },
        [setValue, valuePrefix]
    );

    const handleChangeAmount = useCallback(
        (index: number, amount: number | undefined, service: IService | null, isManual?: boolean) => {
            if (service && service.use_materials && service.materials && isManual) {
                const materialsPrice = getMaterialsBasedServicePrice(service, employeeServices || [], amount);
                handlePriceChange(index, materialsPrice, false);
            }
            setValue(`${valuePrefix}.${index}.materials_amount`, amount);
        },
        [employeeServices, handlePriceChange, setValue, valuePrefix]
    );

    const handleServiceOptionsChange = useCallback(
        (index: number, newOptions: ServiceOption[]) => {
            setValue(`${valuePrefix}.${index}.options`, newOptions);
        },
        [setValue, valuePrefix]
    );

    const selectedServiceIds = useMemo<number[]>(() => {
        const result: number[] = [];
        value.forEach((service) => {
            if (service.service?.id) {
                result.push(service.service.id);
            }
        });

        return result;
    }, [value]);

    const getDifferentLocTypeServiceIds = useCallback(() => {
        const firsServiceLocType = value[0].service?.location_type;
        return firsServiceLocType ? services.filter((service) => service.location_type !== firsServiceLocType).map(({ id }) => id) : [];
    }, [services, value]);

    const getAvailableOptions = useCallback(
        (currentValue?: IService | null) => {
            const selectedOpts = selectedServiceIds.filter((id) => id !== currentValue?.id);
            // If more than one service selected, exclude services with different location type from the list
            const excludedOpts = value.length > 1 ? [...selectedOpts, ...getDifferentLocTypeServiceIds()] : selectedOpts;

            return services.filter((service) => !excludedOpts.includes(service.id));
        },
        [getDifferentLocTypeServiceIds, selectedServiceIds, services, value.length]
    );

    const onAddService = useCallback(() => {
        const defaultService = { service: null, price: null, prepay: null, options: [] };
        setValue(valuePrefix, [...value, defaultService]);
    }, [setValue, value, valuePrefix]);

    const onRemoveService = (index: number) => {
        const newVal = [...value];
        newVal.splice(index, 1);
        setTouched(`${valuePrefix}.${index}.service`, false);
        setTouched(`${valuePrefix}.${index}.prepay`, false);
        setTouched(`${valuePrefix}.${index}.price`, false);
        setTouched(`${valuePrefix}.${index}.materials_amount`, false);
        setValue(valuePrefix, newVal);
    };

    const getFieldError = useCallback(
        (index: number, field: 'service' | 'price' | 'prepay' | 'materials_amount') => {
            const indexError = errors?.[index];
            // @ts-ignore
            const touchedEntry = touched?.[index];
            if (!indexError || typeof indexError === 'string') {
                return null;
            }

            if (!!indexError && typeof indexError !== 'string') {
                return touchedEntry?.[field] ? indexError[field] : null;
            }

            return null;
        },
        [errors, touched]
    );

    const canAddServices = useMemo(() => {
        if (value.length && !useMultiservices) {
            return false;
        }
        const hasAvailableServices = !!getAvailableOptions().length;
        const virtualServiceSelected = value.some((v) => v.service?.location_type === ServiceLocationType.Virtual);

        return hasAvailableServices && !virtualServiceSelected;
    }, [getAvailableOptions, useMultiservices, value]);

    return (
        <Box>
            <SectionHeading mb={0.5}>Services</SectionHeading>
            <Stack spacing={1.5} sx={{ width: '100%' }}>
                {value.map((row, index) => (
                    <Stack spacing={1} key={`${row.service?.id}_${index}`}>
                        <Stack direction="row" spacing={2}>
                            <Box sx={{ flexGrow: 1, flexShrink: 1 }}>
                                <Autocomplete
                                    disableClearable
                                    id={`${valuePrefix}.${index}.service`}
                                    options={getAvailableOptions(row.service)}
                                    loading={isLoading}
                                    value={row.service ?? undefined}
                                    getOptionLabel={(opt) => opt.name}
                                    isOptionEqualToValue={(opt, v) => opt.id === v.id}
                                    onChange={(_e, v) => handleChangeService(v, index)}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Service" error={Boolean(getFieldError(index, 'service'))} />
                                    )}
                                    disabled={disabled}
                                />
                            </Box>
                            <Box sx={{ width: '90px', flexGrow: 0, flexShrink: 0 }}>
                                <NumberFormat
                                    fullWidth
                                    customInput={FocusTextField}
                                    label="Price"
                                    id={`${valuePrefix}.${index}.price`}
                                    name={`${valuePrefix}.${index}.price`}
                                    prefix="$"
                                    decimalScale={2}
                                    allowNegative={false}
                                    value={row.price}
                                    onBlur={() => setTouched(`${valuePrefix}.${index}.price`, true)}
                                    onValueChange={(v, src: SourceInfo) => {
                                        handlePriceChange(index, v.floatValue, !!src.event && src.source !== 'prop');
                                    }}
                                    error={Boolean(getFieldError(index, 'price'))}
                                    disabled={disabled}
                                />
                            </Box>

                            {row.service?.use_materials && row.service.materials && (
                                <Box sx={{ width: '90px', flexGrow: 0, flexShrink: 0 }}>
                                    <NumberFormat
                                        fullWidth
                                        customInput={TextField}
                                        label={`${row.service.materials.name} (${row.service.materials.units})`}
                                        id={`${valuePrefix}.${index}.materials_amount`}
                                        name={`${valuePrefix}.${index}.materials_amount`}
                                        decimalScale={2}
                                        allowNegative={false}
                                        value={row.materials_amount}
                                        onBlur={() => setTouched(`${valuePrefix}.${index}.materials_amount`, true)}
                                        onValueChange={(v, src: SourceInfo) =>
                                            handleChangeAmount(
                                                index,
                                                v.floatValue,
                                                row.service || null,
                                                !!src.event && src.source !== 'prop'
                                            )
                                        }
                                        error={Boolean(getFieldError(index, 'materials_amount'))}
                                        disabled={disabled}
                                    />
                                </Box>
                            )}

                            {/* User can't remove last service */}
                            {value.length > 1 && (
                                <IconButton
                                    color="error"
                                    sx={{
                                        width: '40px',
                                        flexGrow: 0,
                                        flexShrink: 0,
                                        alignSelf: 'center'
                                    }}
                                    onClick={() => onRemoveService(index)}
                                    disabled={disabled}
                                >
                                    <RemoveCircleOutlineOutlinedIcon />
                                </IconButton>
                            )}
                        </Stack>
                        {row.service ? (
                            <AppointmentServiceOptions
                                serviceOptions={row.service.options ?? []}
                                value={row.options}
                                onChange={(v) => handleServiceOptionsChange(index, v)}
                                disabled={disabled}
                            />
                        ) : null}

                        <Box sx={{ '&:empty': { mt: '0 !important' } }}>
                            {getFieldError(index, 'service') && <FormHelperText error>{getFieldError(index, 'service')}</FormHelperText>}
                            {getFieldError(index, 'price') && <FormHelperText error>{getFieldError(index, 'price')}</FormHelperText>}
                            {getFieldError(index, 'prepay') && <FormHelperText error>{getFieldError(index, 'prepay')}</FormHelperText>}
                            {getFieldError(index, 'materials_amount') && (
                                <FormHelperText error>{getFieldError(index, 'materials_amount')}</FormHelperText>
                            )}
                        </Box>
                    </Stack>
                ))}

                {canAddServices && (
                    <Box>
                        <Button
                            variant="text"
                            className="forcedBg"
                            size="small"
                            onClick={onAddService}
                            disabled={disabled}
                            endIcon={<AddCircleOutlineOutlinedIcon />}
                        >
                            Add Service
                        </Button>
                    </Box>
                )}
            </Stack>
        </Box>
    );
};

export default AppointmentServicesSubform;
